import type { DeliveryTracking } from "./types";
import { distanceBetween, type LatLngPoint } from "./smoothing";

/**
 * 把追蹤資料轉成「畫面上要說什麼」——整張卡的文案決策都收斂在這個純函式裡。
 *
 * 為什麼獨立出來：這裡的每一條規則都是為了擋掉一種客訴，不是排版細節，
 * 寫在 JSX 裡就沒人會去驗。有測試在 __tests__/trackingDisplay.test.ts。
 */

export const DISPLAY_THRESHOLDS = {
  /** 進入「即將抵達」的距離（公尺）。再遠的距離估 ETA 不準，只給區間不給時間點。 */
  APPROACHING_M: 2000,
  /** 配下車搬運的判定距離（公尺），需同時 activity 為 on_foot */
  ARRIVED_NEARBY_M: 200,
  /** 超過這個時間沒有新座標 → marker 降透明，並明說「位置更新於 N 分鐘前」 */
  STALE_MS: 3 * 60 * 1000,
  /** 超過這個時間沒有新座標 → 視為配送員關閉定位，整張卡收掉退回原本的狀態文案 */
  ABANDON_MS: 15 * 60 * 1000,
} as const;

export interface TrackingDisplay {
  /** false = 整張卡不渲染（頁面退回原本的 stepMsg） */
  visible: boolean;
  /** false = 只顯示一行文字，不放地圖。沒有座標時放空地圖比不放更糟。 */
  showMap: boolean;
  /**
   * 要不要在配送員與你家之間連那條弧線。
   *
   * 規則是「只有明確知道前面還有別單時才不連」——stopsAhead 為 null（後端還沒帶這個
   * 欄位）時照連。原本的規則是只有 stopsAhead === 0 才連，那個保守版是針對舊的虛直線：
   * 一條筆直的線容易被讀成「他會直直朝你開過來」，前面還有別單時就會誤導。
   * 現在這條是明顯拱起、離地的弧線，讀起來是「這兩個點是一組」而不是「他走這條路」，
   * 誤導的風險低很多；隱私上也不揭露新東西，兩個端點本來就都畫在畫面上了。
   *
   * 但 stopsAhead 真的大於 0 時仍然不連：那時客戶會看到車往反方向開，
   * 連一條線只會讓「他怎麼越開越遠」更刺眼。
   */
  showRouteHint: boolean;
  headline: string;
  sub: string | null;
  /** 位置延遲的分鐘數；> 0 時 marker 要降透明表示「這不是此刻的位置」 */
  staleMinutes: number;
}

/** 800 公尺 / 1.2 公里 —— 公里數只給一位小數，GPS 精度撐不起第二位。 */
export const formatDistance = (meters: number): string =>
  meters < 1000 ? `${Math.round(meters / 10) * 10} 公尺` : `${(meters / 1000).toFixed(1)} 公里`;

const formatEta = (eta: DeliveryTracking["eta"]): string | null => {
  if (!eta?.from) return null;
  // 後端給不出區間上界時退成單一時間點，但文案仍保留「前後」，不做準點承諾
  return eta.to && eta.to !== eta.from ? `預計 ${eta.from}–${eta.to} 抵達` : `預計 ${eta.from} 前後抵達`;
};

const joinParts = (parts: (string | null)[]): string | null => {
  const kept = parts.filter((p): p is string => !!p);
  return kept.length ? kept.join("・") : null;
};

/**
 * @param tracking 後端追蹤資料；null = 還沒載到
 * @param destination 訂單的配送地址座標（address_snapshot_json 的經緯度）
 * @param now 現在時間（epoch ms），測試可注入
 */
export const buildTrackingDisplay = (
  tracking: DeliveryTracking | null,
  destination: LatLngPoint | null,
  now: number = Date.now()
): TrackingDisplay => {
  const position = tracking?.position ?? null;
  const stopsAhead = tracking?.stopsAhead ?? null;
  /** 見 TrackingDisplay.showRouteHint：只有明確大於 0 才不連線 */
  const showRouteHint = stopsAhead === null || stopsAhead === 0;

  // 尚未取得座標：不放空地圖，只用一行字說明狀態正在建立中
  if (!position) {
    return {
      visible: true,
      showMap: false,
      showRouteHint: false,
      headline: "配送員已出發",
      sub: "位置更新中…",
      staleMinutes: 0,
    };
  }

  const ageMs = Math.max(0, now - position.ts);

  // 久到不像只是收訊不好（多半是關閉定位權限）：與其顯示一個早就過時的位置，不如整張卡收掉
  if (ageMs > DISPLAY_THRESHOLDS.ABANDON_MS) {
    return { visible: false, showMap: false, showRouteHint: false, headline: "", sub: null, staleMinutes: 0 };
  }

  const staleMinutes = ageMs > DISPLAY_THRESHOLDS.STALE_MS ? Math.floor(ageMs / 60000) : 0;
  const distance = destination ? distanceBetween(position, destination) : null;
  const etaText = formatEta(tracking?.eta ?? null);

  // 位置過期時，最該講的是「這個點有多舊」，其他細節都退到後面
  if (staleMinutes > 0) {
    return {
      visible: true,
      showMap: true,
      showRouteHint,
      headline: "配送員前往中",
      sub: `位置更新於 ${staleMinutes} 分鐘前`,
      staleMinutes,
    };
  }

  // 已下車搬運：這段（找車位、搬鋼瓶、爬樓梯）常佔 5–10 分鐘，也正是客戶最焦躁的時候。
  // 明講「在搬了」比繼續倒數任何時間都有效。
  if (position.activity === "on_foot" && distance !== null && distance < DISPLAY_THRESHOLDS.ARRIVED_NEARBY_M) {
    return {
      visible: true,
      showMap: true,
      showRouteHint,
      headline: "配送員已抵達附近",
      sub: "正搬運前往您的地址",
      staleMinutes: 0,
    };
  }

  const distanceText = distance !== null ? `距離 ${formatDistance(distance)}` : null;

  if (distance !== null && distance < DISPLAY_THRESHOLDS.APPROACHING_M) {
    return {
      visible: true,
      showMap: true,
      showRouteHint,
      headline: "配送員即將抵達",
      sub: joinParts([etaText, distanceText]),
      staleMinutes: 0,
    };
  }

  // 前面還有別單時，先講這件事——它解釋了「車子為什麼往反方向開」，
  // 能直接擋掉一通電話。但只講數量，不得帶出其他訂單的任何內容。
  const queueText = stopsAhead != null && stopsAhead > 0 ? `前面還有 ${stopsAhead} 單` : null;

  return {
    visible: true,
    showMap: true,
    showRouteHint,
    headline: "配送員前往中",
    // 註：isMoving === false 時刻意不顯示「停止中」——客戶會腦補成塞車或偷懶，
    // 但等紅燈、單行道繞路都會讓它變 false，講了只會製造焦慮。
    sub: joinParts([queueText, etaText, distanceText]),
    staleMinutes: 0,
  };
};
